/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useEffect, useRef, ReactElement, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { getColor, getTextColor, getBorders, getPatternModal, getBackgroundTheme } from '../../../style';

import Avatar from '../../../atoms/Avatar';
import Headline from '../../common/Headline';
import TextLink from '../../common/TextLink';
import PriceInput from './PriceInput';
import OfferPriceBtnSet from './OfferPriceBtnSet';
import Button from '../../../atoms/buttons/Button';
import ButtonSet from '../../blocks/ButtonSet';
import { addCommas, areEqual } from '../../../utils/helpers';
import {
  sendOffer,
  sendCancel,
  sendSuggest,
  sendAmountCustom,
  counterOffer,
  counterCancel,
  counterModalCustomAmount,
} from '../../../actions/profileTrackAction';
import useOnClickTriggerTracking from '../../../hooks/useOnclickTriggerTracking';
import { getDefaultOfferAmount } from '../../../utils/offers';
import { displayWarnMessage } from '../../../actions/notificationActions';
import { useDispatch, useSelector } from 'react-redux';
import { clearErrors } from '../../../actions/commonActions';
import { RootState } from '../../../store';
import { defaultOfferAmount } from '../../../common';
import useMandatoryWall from '../../../hooks/useMandatoryWall';
import breakpoints from 'theme/material/breakpoints';

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const HeaderDiv = styled('div')({
  textAlign: 'center',
  marginTop: 42,

  [breakpoints.up(640)]: {
    marginTop: 12,
  },
});

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  justifyContent: 'space-between',
});

const setColor = {
  default: {
    color: 'grey',
  },
  brand: {
    color: getColor().BRAND_1,
  },
  alert: { ...getTextColor().RED },
};

const colorType = (price, hasErrors) => {
  switch (price) {
    case true:
      return 'default';
    case false:
      return !hasErrors ? 'brand' : 'alert';
    default:
      return;
  }
};

const CurrencyTip = styled('div')<{ offerPrice: string }>(
  {
    display: 'inline-block',
    position: 'relative',
  },
  ({ offerPrice }) => ({
    ...setColor[offerPrice],
    ':before': {
      position: 'absolute',
      top: 14,
      right: '85%',
      fontSize: 22,
    },
  })
);

const CounterRange = styled('div')({
  fontSize: '13px',
  marginBottom: '12px',
  textAlign: 'center',
});

const PriceContainer = styled('div')<{ hasErrors: boolean }>(
  {
    background: 'red',
    textAlign: 'center',
    width: '260px',
    margin: '20px auto',
    borderRadius: '2px',
  },

  ({ hasErrors }) => ({
    backgroundColor: hasErrors ? 'rgba(204, 84, 84,0.15)' : getBackgroundTheme().fsModal,
    border: hasErrors ? '1px solid #cc5454' : '1px solid #5BB1F1',
  })
);

const ErrorContainer = styled('div')({
  textAlign: 'center',
  marginTop: '16px',
});

const ErrorMessage = styled('div')({
  color: 'rgb(204, 84, 84)',
  margin: '16px auto 0',
  maxWidth: '280px',
});

const PurchaseCredits = styled('div')({
  textAlign: 'center',
  marginTop: '12px',
  textDecoration: 'underline',
});

const ButtonDiv = styled('div')({
  marginTop: 22,
  [breakpoints.down(639.05)]: {
    marginBottom: 10,
    paddingTop: 12,
    borderTop: getBorders().DEFAULT,
    width: '100%',
  },
});

const OfferAmounts = [50, 75, 100, 150, 200];

const getInputProps = () => ({
  style: {
    fontSize: 55,
    height: 72,
    lineHeight: '72px',
    fontWeight: 300,
    outline: 'none',
    border: 'none',
    color: getPatternModal().amtColor,
  },
});

const getHeadline = (label, currInitial, offer, currentUserIsGenerous, isCounter, username) => {
  const hasOffer =
    offer && !offer.suggest && offer.price_local && offer.price_original && offer.price_original !== null;

  const offerDescription = currentUserIsGenerous && isCounter ? 'request' : 'offer';

  if (isCounter || hasOffer) {
    const price_original = offer && offer.price_original ? offer.price_original : 0;

    return `Counter ${currInitial}${label}${price_original} ${offerDescription} from ${username}`;
  } else {
    return `Request a date with ${username}`;
  }
};

const status = currentUserIsGenerous => (currentUserIsGenerous ? 'Offer' : 'Request');

const offerMinMaxVal = (offer, type, defaultAmount) => {
  const offerVal = offer && type === 'min' ? offer.min_counter_price : offer && offer.max_counter_price;

  return offer ? offerVal : defaultAmount;
};

const validateTextInput = e => {
  const Key = e.key;
  const regex = /[0-9]/;

  if (!regex.test(Key)) {
    if (e.preventDefault) e.preventDefault();
  }
};

const renderCounterRange = (isCounter, currencyInitial, currLabel, minValue, maxValue) => {
  if (isCounter) {
    return (
      <CounterRange>
        min {currencyInitial}
        {currLabel}
        {minValue} max {currencyInitial}
        {currLabel}
        {maxValue}
      </CounterRange>
    );
  }
  return null;
};

const setCreateOfferAction = (_e: Event, offerAction: string) => {
  switch (offerAction) {
    case 'Send Counter':
      counterOffer();
      break;
    case 'Cancel Counter':
      counterCancel();
      break;
    case 'Send Offer':
      sendOffer();
      break;
    case 'Cancel Offer':
      sendCancel();
      break;
    case 'Suggest Offer':
      sendSuggest();
      break;
    default:
      return;
  }
};

const trackFocus = (ref, actionMethod) => {
  if (ref && ref.current) {
    ref.current.addEventListener('click', e => actionMethod(e), false);
  }

  return () => {
    ref.current.removeEventListener('click', () => null, false);
  };
};

interface OfferCreateProps {
  avatarSrc: any;
  offer: any;
  isCounter: any;
  closeModal: any;
  currencyLabel: any;
  currencyInitial: any;
  currentUserIsGenerous: any;
  sex: any;
  justSuggested: any;
  ignoreStatus: any;
  username: any;
  sendSuggestion: any;
  cannot_make_offer_reasons: any;
  counterOffer: any;
  createNewOffer: any;
  hashId: any;
  buttonID: any;
  otherProfile: any;
}

const OfferCreate = ({
  avatarSrc,
  offer,
  isCounter,
  closeModal,
  currencyLabel,
  currencyInitial,
  currentUserIsGenerous,
  sex,
  justSuggested,
  ignoreStatus,
  username,
  sendSuggestion,
  cannot_make_offer_reasons,
  counterOffer,
  createNewOffer,
  hashId,
  buttonID,
  otherProfile,
}: OfferCreateProps): ReactElement => {
  const dispatch = useDispatch();

  const errors = useSelector((state: RootState) => state.common.errors);

  const defaultOfferPrice = useMemo(() => {
    const defaultAmount = getDefaultOfferAmount();

    if (otherProfile.account_type === 'Attractive') {
      return otherProfile?.default_offer_amount ?? defaultAmount;
    }

    return defaultAmount;
  }, [otherProfile]);

  const { showMandatoryWall } = useMandatoryWall();

  const [defaultValue] = useState('');
  const [offerPrice, setOfferPrice] = useState(isCounter ? '' : defaultOfferPrice);
  const [minValue, setMinValue] = useState(offerMinMaxVal(offer, 'min', defaultOfferAmount.minValue));
  const [maxValue, setMaxValue] = useState(offerMinMaxVal(offer, 'max', defaultOfferAmount.maxValue));
  const [hasErrors, setHasErrors] = useState(false);
  const [isOfferSending, setOfferSending] = useState(false);
  const sendOfferBtn = useRef(null);
  const sendCancelBtn = useRef(null);
  const sendSuggestLink = useRef(null);
  const priceInput = useRef(null);

  useOnClickTriggerTracking(isCounter ? 'Send Counter' : 'Send Offer', sendOfferBtn, setCreateOfferAction);
  useOnClickTriggerTracking(isCounter ? 'Cancel Counter' : 'Cancel Offer', sendCancelBtn, setCreateOfferAction);
  useOnClickTriggerTracking('Suggest Offer', sendSuggestLink, setCreateOfferAction);

  const handleClearErrors = useCallback(() => {
    dispatch(clearErrors());
  }, [dispatch]);

  useEffect(() => {
    handleClearErrors();
  }, [handleClearErrors]);

  useEffect(() => {
    trackFocus(priceInput, isCounter ? counterModalCustomAmount : sendAmountCustom);
  }, [isCounter]);

  useEffect(() => {
    setMaxValue(offerMinMaxVal(offer, 'max', defaultOfferAmount.maxValue));
    setMinValue(offerMinMaxVal(offer, 'min', defaultOfferAmount.minValue));
  }, [offer]);

  const updateOfferPrice = (val, error) => {
    if (!error) {
      handleClearErrors();
    }

    setOfferPrice(val);
    setHasErrors(error || false);
  };

  const inputOnChange = e => {
    const InputVal = e.target.value;
    const MaxLength = maxValue.toString().length;

    if (InputVal.length <= MaxLength) {
      const Error = Boolean(Number(InputVal) < Number(minValue) || Number(InputVal) > Number(maxValue));

      updateOfferPrice(InputVal, Error);
    } else {
      e.preventDefault();
    }
  };

  const currLabel = String.fromCharCode(currencyLabel);

  const mixpanelSource = () => {
    switch (buttonID) {
      case 'counterOfferHighlights':
        return 'Modal';
      case 'button-offer-create':
        return 'Profile';
      default:
        return 'Received Offers';
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = { price: offerPrice, account: hashId, source: mixpanelSource() };

    setOfferSending(true);
    showMandatoryWall(
      () => {
        if (isCounter || (offer && offer.suggest)) {
          counterOffer(formData)
            .then(() => {
              closeModal();
              setOfferSending(false);
            })
            .catch(() => {
              setOfferSending(false);
              setHasErrors(true);
            });
        } else {
          createNewOffer(formData)
            .then(() => {
              closeModal();
              setOfferSending(false);
            })
            .catch(() => {
              setOfferSending(false);
              setHasErrors(true);
            });
        }
      },
      () => setOfferSending(false),
      isCounter || (offer && offer.suggest) ? 'Counter Offer' : 'Make Offer'
    );
  };

  const formatSubmitButtonText = () => {
    return isOfferSending ? `Sending ${status(currentUserIsGenerous)}...` : `Send ${status(currentUserIsGenerous)}`;
  };

  const onClickRenderSuggestionBtn = (e, cannot_make_offer_reasons, sendSuggestion) => {
    e.preventDefault();
    // check to see if user can make an offer before allowing it.
    if (cannot_make_offer_reasons === null) {
      // process suggestion
      sendSuggestion();
    } else {
      // explain suggestion fail reason.
      dispatch(
        displayWarnMessage({
          info: `You cannot send an offer suggestion: ${cannot_make_offer_reasons[0]}`,
        })
      );
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <HeaderDiv>
        <Avatar
          data-test-id="offer-modal-avatar"
          src={avatarSrc}
          width={'72px'}
          customStyle={{
            backgroundColor: '#ccc',
            margin: '0 auto',
            marginBottom: 18,
          }}
        />
        <Headline data-test-id="profile-offer-headline" size="SM">
          {getHeadline(currLabel, currencyInitial, offer, currentUserIsGenerous, isCounter, username)}
        </Headline>
        <div data-test-id="profile-offer-desc">
          {currentUserIsGenerous ? 'Offers' : 'Requests'} can be up to{' '}
          {
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            currencyInitial + currLabel + addCommas(parseInt(maxValue, 10))
          }
        </div>
      </HeaderDiv>
      <Content>
        <div>
          <PriceContainer hasErrors={hasErrors}>
            <CurrencyTip
              offerPrice={colorType(offerPrice, hasErrors)}
              css={{
                ':before': {
                  content: `'${currencyInitial}${currLabel}'`,
                },
              }}
            >
              <PriceInput
                inputRef={priceInput}
                data-test-id="offer-price-input"
                hasErrors={hasErrors}
                value={offerPrice}
                onChange={inputOnChange}
                validateTextFn={validateTextInput}
                placeholder={defaultValue}
                inputProps={getInputProps()}
              />
            </CurrencyTip>
            {renderCounterRange(isCounter, currencyInitial, currLabel, minValue, maxValue)}
          </PriceContainer>
          <OfferPriceBtnSet
            prices={OfferAmounts}
            updateOfferPrice={updateOfferPrice}
            currentAmount={offerPrice}
            currLabel={currLabel}
            currInitial={currencyInitial}
            offer={offer}
            isCounter={isCounter}
          />
          {errors ? (
            <ErrorContainer>
              <ErrorMessage>{errors.price}</ErrorMessage>
              {currentUserIsGenerous && Number(offerPrice) >= 200 && (
                <PurchaseCredits>
                  <TextLink to="/packages">Purchase credits now.</TextLink>
                </PurchaseCredits>
              )}
            </ErrorContainer>
          ) : null}
        </div>
        <ButtonDiv
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          marginTop={16}
        >
          <ButtonSet align="center">
            <Button
              buttonRef={sendCancelBtn}
              data-test-id={`${buttonID}Cancel`}
              disabled={isOfferSending}
              onClick={e => {
                e.preventDefault();
                closeModal();
              }}
            >
              {' '}
              Cancel
            </Button>
            <Button
              id={buttonID}
              buttonRef={sendOfferBtn}
              disabled={hasErrors || isOfferSending}
              buttonType="primaryRed"
              onClick={handleSubmit}
              type="submit"
            >
              {formatSubmitButtonText()}
            </Button>
          </ButtonSet>
          {!(offer || justSuggested || ignoreStatus) && (
            <span>
              <div style={{ textAlign: 'center', padding: '8px 0' }}>or</div>
              <ButtonSet align="center">
                <TextLink
                  linkRef={sendSuggestLink}
                  data-test-id="offer-suggest-link"
                  to="/"
                  disabled={hasErrors || isOfferSending}
                  onClick={e => onClickRenderSuggestionBtn(e, cannot_make_offer_reasons, sendSuggestion)}
                  type="submit"
                >
                  {' '}
                  Ask {sex === 'Male' ? 'him' : 'her'} to suggest an amount
                </TextLink>
              </ButtonSet>
            </span>
          )}
        </ButtonDiv>
      </Content>
    </Form>
  );
};

export default React.memo(OfferCreate, areEqual);
